import { Component, OnInit } from '@angular/core';
import { Project } from 'src/app/models/Project';
import { ProjectsService } from 'src/app/services/projects.service';
import { AlertController, ModalController } from '@ionic/angular';
import { MessagesService } from 'src/app/services/messages.service';
import { UnfocusElementService } from 'src/app/services/unfocus-element.service';
import { ExportParameters } from 'src/app/models/export/ExportParameters';
import { CustomerUsageService } from 'src/app/services/customer-usage.service';
import { AutoSaveService } from 'src/app/services/auto-save.service';
import { CryptoService } from 'src/app/services/crypto.service';
import { StorageService } from 'src/app/services/storageService.service';

@Component({
  selector: 'app-export-project-wizard-modal',
  templateUrl: './export-project-wizard-modal.page.html',
  styleUrls: ['./export-project-wizard-modal.page.scss'],
})
export class ExportProjectWizardModalPage implements OnInit {

  public project: Project;
  public exportStep: number = 1;
  public exportParameters: ExportParameters;
  public streamingOptionDisabled: boolean = false;
  public inviteLearnersFormatDisabled: boolean = false;
  public usageWarningMessages: any = {
    color: 'danger',
    format: '',
    options: '',
    distribute: ''
  };

  constructor(
    public unfocusService: UnfocusElementService,
    public projectService: ProjectsService,
    public customerUsageService: CustomerUsageService,
    public modalController: ModalController,
    public alertController: AlertController,
    public messagesService: MessagesService,
    public autoSaveService: AutoSaveService,
    public cryptoService: CryptoService,
    public storageService: StorageService
  ) { }

  ngOnInit() {
  }

  ionViewWillEnter() {
    // INITS EXPORT WIZARD
    this.exportParameters = new ExportParameters();
    this.exportParameters.exportFormat = 'LMS (Scorm 1.2/2004)';
    this.exportParameters.streaming = false;
    this.exportParameters.lrsApiUrl = '';
    this.exportParameters.companionApp = false;
    this.exportParameters.learners = [];
    this.exportParameters.emailText = `
      VR Assessment Invitation, {learnerFirstName} {learnerLastName}, <br/><br/><br/>
      You have been sent an invitation to partake in the {projectName} virtual reality(VR) assessment on your VR headset.<br/><br/>
      This assessment can be accessed from the following link: <br/> {invitationUrl} <br/><br/>
      This is a unique link to your assessment, so please do not share this link with anyone else.<br/><br/><br/>`;
    this.exportParameters.isEmailEditorOpen = false;

    this.validateLRSAndStreamingCredits();
  }

  closeModal() {
    this.modalController.dismiss();
  }

  async nextStep(event) {
    this.unfocusService.unfocus(event);

    if (this.exportStep == 1) { // IS THE USER IS IN EXPORT FORMAT TAB
      this.streamingOptionDisabled = false;
      if (this.exportParameters.exportFormat === 'Invite Learners') {
        this.exportParameters.streaming = true;
        this.streamingOptionDisabled = true;
      }
      else if (this.exportParameters.exportFormat !== 'Invite Learners' && this.exportParameters.companionApp !== true) {
        this.exportParameters.streaming = false;
        this.exportParameters.companionApp = false;
      }
      this.validateLRSAndStreamingCredits();
      this.exportStep++;

    } else if (this.exportStep == 2) { // IS THE USER IS IN EXPORT FORMAT EXPORT OPTIONS TAB
      this.exportParameters.isEmailEditorOpen = false;
      this.exportStep++;
    }
  }

  previousStep(event) {
    this.unfocusService.unfocus(event);
    this.exportStep--;
  }

  goToStep(step: number) {
    this.exportStep = step;
  }

  async export(event) {
    this.unfocusService.unfocus(event);

    let remainingStreamingCredits = this.customerUsageService.customerUsageLimit.streamingUsage - this.customerUsageService.customerUsage.streamingUsage;
    let neededStreamingCredits = this.exportParameters.learners.length * this.project.projectSize;

    if (this.exportParameters.streaming == true && neededStreamingCredits >= remainingStreamingCredits) {
      const alert = await this.alertController.create({
        header: 'Export Project',
        message: "You don't have enough distribution credits to send invitations.<br/> Please remove some learners from the invitation list or contact your system administrator.",
        cssClass: 'leave-alert',
        buttons: [{
          text: 'OK',
          cssClass: 'leave-button',
          handler: () => {
          }
        }]
      });
      await alert.present();
      return;
    }

    await this.messagesService.presentLoadingWindow('Downloading Project...');
    this.projectService.exportProject(this.project, this.exportParameters).then(async projectUrl => {
      await this.refreshCustomerUsageCredits().then((result) => {
        let message = 'Project exported successfully!';
        window.location.href = projectUrl;

        this.messagesService.loadingWindow.dismiss().then(async () => {
          const alert = await this.alertController.create({
            header: 'Export Project',
            message: message,
            cssClass: 'leave-alert',
            backdropDismiss: false,
            buttons: [{
              text: 'OK',
              cssClass: 'leave-button',
              handler: () => {
                this.modalController.dismiss();
              }
            }]
          });
          await alert.present();
        });
      });
    });
  }

  async inviteLearners(event) {
    this.unfocusService.unfocus(event);
    if (this.exportParameters.learners.length == 0) {
      const alert = await this.alertController.create({
        header: 'Export Project',
        message: 'At least 1 learner has to be selected.',
        cssClass: 'leave-alert',
        buttons: [{
          text: 'OK',
          cssClass: 'leave-button',
          handler: () => {
          }
        }]
      });
      await alert.present();
      return;
    }

    let remainingStreamingCredits = this.customerUsageService.customerUsageLimit.streamingUsage - this.customerUsageService.customerUsage.streamingUsage;
    let neededStreamingCredits = this.exportParameters.learners.length * this.project.projectSize;

    if (neededStreamingCredits >= remainingStreamingCredits) {
      const alert = await this.alertController.create({
        header: 'Export Project',
        message: "You don't have enough distribution credits to send invitations.<br/> Please remove some learners from the invitation list or contact your system administrator.",
        cssClass: 'leave-alert',
        buttons: [{
          text: 'OK',
          cssClass: 'leave-button',
          handler: () => {
          }
        }]
      });
      await alert.present();
      return;
    }

    await this.messagesService.presentLoadingWindow('Sending Invitations...');
    this.projectService.sendInvitationsToLearners(this.project, this.exportParameters).then(async projectResponse => {
      await this.refreshCustomerUsageCredits().then((result) => {
        let message = 'Invitations sent!';

        this.messagesService.loadingWindow.dismiss().then(async () => {
          const alert = await this.alertController.create({
            header: 'Export Project',
            message: message,
            cssClass: 'leave-alert',
            backdropDismiss: false,
            buttons: [{
              text: 'OK',
              cssClass: 'leave-button',
              handler: () => {
                this.modalController.dismiss();
              }
            }]
          });
          await alert.present();
        });
      });
    });
  }

  validateLRSAndStreamingCredits() {
    if (this.customerUsageService.calculateUsagePercentage('LRS') >= 100 || this.customerUsageService.calculateUsagePercentage('Streaming') >= 100) {
      this.streamingOptionDisabled = true;
      this.inviteLearnersFormatDisabled = true;
      this.usageWarningMessages.color = 'danger';
      this.usageWarningMessages.format = `You have used all your distribution or reporting credits. You won't be able to use the "Invite Learners" Option`;
      this.usageWarningMessages.options = 'You have used all your distribution or reporting credits.';
    } else if (this.customerUsageService.calculateUsagePercentage('LRS') >= 80 || this.customerUsageService.calculateUsagePercentage('Streaming') >= 80) {
      this.usageWarningMessages.color = 'warning';
      this.usageWarningMessages.format = 'Your distribution or reporting credits are running low.';
      this.usageWarningMessages.options = 'Your distribution or reporting credits are running low.';
    }
  }

  refreshCustomerUsageCredits(): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      let customerId: number = Number(this.cryptoService.decryptData(this.storageService.getItem('customerId')));
      await this.customerUsageService.getCustomerUsage(customerId);
      let lrsCredits: number = this.customerUsageService.customerUsageLimit.maxLrsUsers - this.customerUsageService.customerUsage.lrsUsage;
      let streamingCredits: number = this.customerUsageService.customerUsageLimit.streamingUsage - this.customerUsageService.customerUsage.streamingUsage
      resolve(lrsCredits > 0 && streamingCredits > 0);
    });
  }

  cancelEmailText(event){
    this.unfocusService.unfocus(event);
    this.exportParameters.isEmailEditorOpen = false;
  }

  saveEmailText(event){
    this.unfocusService.unfocus(event);
    this.exportParameters.isEmailEditorOpen = false;
    this.exportParameters.emailText = this.exportParameters.emailTextEdit;
  }
}
